import { useState, useEffect } from "react";
import { useMappedInSettings } from "../../hooks/useMappedInSettings";

const LOGO_URL = "https://paulavatar.s3.us-east-2.amazonaws.com/ActOneGroup-SP-Tall_500px_1742915426318.png";
const SPLASH_DURATION = 3000; // ms logo is shown before map appears

// Default fallback values
const DEFAULT_BASE_URL = "https://web.mappedin.com/lga-laguardia/laguardia-airport.html";



const MappedInPathDisplay = ({
  externalId,
  locationName,
  brandColor = "#1976d2",
  logoBeforeMap,
  isMobile = false,
  onMapInitialized,
  onError,
  mapLink,
  currentLanguage = 'en',
  userId
}) => {

  // Fetch MappedIn settings to get dynamic start location
  const { settings: mappedInConfig, loading: configLoading } = useMappedInSettings(userId);

  const buildMapUrl = (externalId, baseUrl) => {
    const startLocation = mappedInConfig?.mappedInStartLocation || "LOC-1IP30ZO0";
    const base = (baseUrl || DEFAULT_BASE_URL).split('#')[0].replace(/\/$/, '');
    return `${base}#/directions?to=${externalId}&from=${startLocation}&lang=${currentLanguage}`;
  };

  const [showSplash, setShowSplash] = useState(false); // Will be set to true if logoBeforeMap is enabled
  const [hasError, setHasError] = useState(false);

  // Auto-hide splash after SPLASH_DURATION ms or custom duration
  useEffect(() => {
    console.log("🖼️ MappedInPathDisplay logoBeforeMap:", logoBeforeMap);
    
    // Only show splash if logoBeforeMap is enabled and has a file URL
    if (logoBeforeMap?.enabled && logoBeforeMap?.fileUrl) {
      console.log("✅ Showing logo before map:", {
        fileUrl: logoBeforeMap.fileUrl,
        width: logoBeforeMap.width,
        displayDuration: logoBeforeMap.displayDuration
      });
      
      setShowSplash(true);
      
      const splashDuration = logoBeforeMap.displayDuration 
        ? logoBeforeMap.displayDuration * 1000 
        : SPLASH_DURATION;
      
      console.log(`⏱️ Logo will show for ${splashDuration}ms`);
      
      const timer = setTimeout(() => {
        console.log("⏰ Hiding logo, showing map");
        setShowSplash(false);
      }, splashDuration);
      return () => clearTimeout(timer);
    } else {
      console.log("❌ Logo before map not enabled or no file URL");
      setShowSplash(false);
    }
  }, [logoBeforeMap]);

  if (!externalId?.trim()) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f8f9fa",
          color: "#6c757d",
          textAlign: "center",
          padding: "20px",
          borderRadius: isMobile ? "8px" : "15px",
        }}
      >
        <div style={{ fontSize: isMobile ? "32px" : "48px", marginBottom: "16px" }}>🗺️</div>
        <div style={{ fontSize: isMobile ? "14px" : "16px", fontWeight: "bold" }}>
          Map not available
        </div>
        <div style={{ fontSize: isMobile ? "12px" : "13px", marginTop: "8px", opacity: 0.7 }}>
          No destination configured for {locationName || "this location"}
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f8f9fa",
          color: "#e74c3c",
          textAlign: "center",
          padding: "20px",
          borderRadius: isMobile ? "8px" : "15px",
        }}
      >
        <div style={{ fontSize: isMobile ? "32px" : "48px", marginBottom: "16px" }}>⚠️</div>
        <div style={{ fontSize: isMobile ? "14px" : "16px", fontWeight: "bold" }}>
          Map failed to load
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>

      {/* Logo splash — shown for SPLASH_DURATION, then fades out */}
      {(logoBeforeMap?.enabled && logoBeforeMap?.fileUrl) && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#ffffff",
            zIndex: 10,
            borderRadius: isMobile ? "8px" : "15px",
            opacity: showSplash ? 1 : 0,
            pointerEvents: showSplash ? "auto" : "none",
            transition: "opacity 0.4s ease",
          }}
        >
          <img
            src={logoBeforeMap?.fileUrl}
            alt="Logo"
            style={{
              width: `${logoBeforeMap.width || 100}%`,
              maxWidth: isMobile ? "200px" : "300px",
              objectFit: "contain",
            }}
          />
        </div>
      )}

      {/* Crop wrapper — hides the iframe's built-in header and footer bars */}
      <div
        style={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          borderRadius: isMobile ? "8px" : "15px",
          position: "relative",
        }}
      >
        <iframe
          src={buildMapUrl(externalId.trim(), mapLink)}
          title={`Directions to ${locationName || externalId}`}
          onLoad={() => {
            if (onMapInitialized) onMapInitialized();
          }}
          onError={() => {
            setHasError(true);
            if (onError) onError(new Error("Iframe failed to load"));
          }}
          style={{
            /* Make iframe taller than container so we can shift it up,
               cropping the top header (~70px) and bottom bar (~120px) */
            width: "100%",
            height: "calc(100% + 590px)",
            marginTop: "-350px",
            border: "none",
            display: "block",
          }}
          allowFullScreen
        />
      </div>
    </div>
  );
};

export default MappedInPathDisplay;